import React, { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";

// Lazy load all route components
const HubDashboard = lazy(
  () => import("../../modules/hub/features/HubDashboard/HubDashboard"),
);
const SiteIndex = lazy(
  () => import("../../modules/hub/features/SiteIndex/SiteIndex"),
);
const MonthlyBalanceSheet = lazy(
  () =>
    import(
      "../../modules/hub/features/FinancialReports/components/MonthlyBalanceSheet/MonthlyBalanceSheet"
    ),
);
const ProfitAndLoss = lazy(
  () =>
    import(
      "../../modules/hub/features/FinancialReports/components/ProfitAndLoss/ProfitAndLoss"
    ),
);
const ProfitAndLossBuilder = lazy(
  () =>
    import(
      "../../modules/hub/features/FinancialReports/components/ProfitAndLossBuilder/ProfitAndLossBuilder"
    ),
);
const TransactionList = lazy(
  () =>
    import(
      "../../modules/hub/features/FinancialReports/components/TransactionList/TransactionList"
    ),
);
const DataConnector = lazy(
  () =>
    import(
      "../../modules/hub/features/HotglueDataConnector/components/DataConnector/DataConnector"
    ),
);
const UserList = lazy(
  () =>
    import(
      "../../modules/hub/features/UserManagement/components/UserList/UserList"
    ),
);
const EditUser = lazy(
  () =>
    import(
      "../../modules/hub/features/UserManagement/components/EditUser/EditUser"
    ),
);
const CreateUser = lazy(
  () =>
    import(
      "../../modules/hub/features/UserManagement/components/CreateUser/CreateUser"
    ),
);
const EditBusinessRolesAndPermissionsList = lazy(
  () =>
    import(
      "../../modules/hub/features/UserManagement/components/EditBusinessRolesAndPermissionsList/EditBusinessRolesAndPermissionsList"
    ),
);
const SettingsLayout = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/SettingsLayout/SettingsLayout"
    ),
);
const UserProfile = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/UserProfile/UserProfile"
    ),
);
const CompanyProfile = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/CompanyProfile/CompanyProfile"
    ),
);
const ServiceTitanSettings = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/ServiceTitanSettings/ServiceTitanSettings"
    ),
);
const QuickBooksSettings = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/QuickBooksSettings/QuickBooksSettings"
    ),
);
const AnnualRevenue = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/AnnualRevenue/AnnualRevenue"
    ),
);
const Integrations = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/Integrations/Integrations"
    ),
);
const Settings = lazy(
  () =>
    import(
      "../../modules/hub/features/UserAppSettings/components/Settings/Settings"
    ),
);
const CoachingDashboard = lazy(
  () =>
    import(
      "@/modules/hub/features/CoachingDashboard/components/CoachingDasboard/CoachingDashboard"
    ),
);
const ResourceLibrary = lazy(() =>
  import(
    "@/modules/hub/features/ResourceLibrary/components/ResourceLibrary/ResourceLibrary"
  ).then((module) => ({
    default: module.ResourcesLibrary,
  })),
);
const ResourceLibraryV2 = lazy(() =>
  import(
    "@/modules/hub/features/ResourceLibrary/components/ResourceLibraryV2/ResourceLibraryV2"
  ).then((module) => ({
    default: module.ResourceLibraryV2,
  })),
);
const ResourceDetails = lazy(
  () =>
    import(
      "@/modules/hub/features/ResourceLibrary/components/ResourceDetails/ResourceDetails"
    ),
);
const ResourceCategoryList = lazy(
  () =>
    import(
      "@/modules/hub/features/ResourceCategoryManagement/components/ResourceCategoryList/ResourceCategoryList"
    ),
);
const ResourceTagList = lazy(
  () =>
    import(
      "@/modules/hub/features/ResourceTagManagement/components/ResourceTagList/ResourceTagList"
    ),
);
const Podcasts = lazy(() =>
  import("@/modules/hub/features/Podcasts/components/Podcasts/Podcasts").then(
    (module) => ({
      default: module.Podcasts,
    }),
  ),
);
const News = lazy(() =>
  import("@/modules/hub/features/News/components/News/News").then((module) => ({
    default: module.News,
  })),
);
const LeadGeneration = lazy(
  () =>
    import(
      "@/modules/hub/features/LeadGeneration/components/LeadGeneration/LeadGeneration"
    ),
);

// Create stable component reference
const HubMainLayout = () => <MainLayout section="hub" />;

/**
 * HubRoutes
 *
 * Wraps the /hub section in the Auth0-based authentication guard, then
 * protects individual routes with PermissionGuard based on the required
 * permissions from your navigation config.
 */
const HubRoutes = (
  <Route
    element={<AuthenticationGuard component={HubMainLayout} />}
    path="/hub"
  >
    <Route element={<HubDashboard />} index />

    <Route element={<CoachingDashboard />} path="dashboards/coaching" />

    {/* Financial Reports routes */}
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-monthly-balance"]}
        >
          <MonthlyBalanceSheet />
        </PermissionGuard>
      }
      path="financial-reports/monthly-balance-sheet"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-profit-and-loss"]}
        >
          <ProfitAndLoss />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-p-and-l-report-builder"]}
        >
          <ProfitAndLossBuilder />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss-builder"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-transaction-list"]}
        >
          <TransactionList />
        </PermissionGuard>
      }
      path="financial-reports/transaction-list"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["data-connector"]}>
          <DataConnector />
        </PermissionGuard>
      }
      path="data-connector"
    />

    {/* User Management routes - accessible via user-management permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <UserList />
        </PermissionGuard>
      }
      path="users"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <CreateUser />
        </PermissionGuard>
      }
      path="users/create"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <EditUser />
        </PermissionGuard>
      }
      path="users/:uuid/edit"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EditBusinessRolesAndPermissionsList />
        </PermissionGuard>
      }
      path="users/business-roles-permissions"
    />

    {/* Settings - nested routes with side navigation */}
    <Route element={<SettingsLayout />} path="settings">
      <Route element={<Navigate replace to="profile" />} index />
      <Route element={<UserProfile />} path="profile" />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <CompanyProfile />
          </PermissionGuard>
        }
        path="company"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <ServiceTitanSettings />
          </PermissionGuard>
        }
        path="integrations/servicetitan"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <QuickBooksSettings />
          </PermissionGuard>
        }
        path="integrations/quickbooks"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["annual-revenue"]}>
            <AnnualRevenue />
          </PermissionGuard>
        }
        path="annual-revenue"
      />
    </Route>

    {/* Site Index - every app and page the user can open, on one screen */}
    <Route element={<SiteIndex />} path="index" />

    <Route element={<ResourceLibrary />} path="resources" />
    <Route element={<ResourceLibraryV2 />} path="resources/v2" />
    <Route element={<ResourceDetails />} path="resources/:slug" />

    <Route element={<News />} path="news" />
    <Route element={<Podcasts />} path="podcasts" />

    <Route element={<LeadGeneration />} path="lead-generation" />
  </Route>
);

export default HubRoutes;
